import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {Patient, Questionnaire, CustomFieldDefinition, CustomFieldsLayout, MasterDataLayout, MasterDataFieldPosition} from '../../../types';
import {configKeys} from '../patient.config-keys';


@Injectable()
export class PatientService {
  /**
   * Locale for patients. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  public constructor(
    private registryService: RegistryService,
    private configService: ConfigService,
    private http: HttpClient
  ) {
  }

  /**
   * Loads patients
   *
   * @param filter: any
   * @returns {Promise<Patient[]>}
   */
  public loadPatients(filter?: Patient.Filter, opts?: Patient.Opts): Promise<{rows: Patient[], total: number}> {
    const query: any = {filter: JSON.stringify(filter) || '{}', opts: JSON.stringify(opts) || '{}'};
    const basePath = `${this.getPatientApiUrl()}?${decodeURI(new URLSearchParams(query).toString())}`;

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe((patient: any) => {
            const patients = patient.patient || patient.rows || patient || [];
            const total = patient.total || 0;

            resolve({rows: patients, total});
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Helper method to load patient by id.
   *
   * @param {string} patientId
   * @returns {Promise<Patient>}
   */
  public loadPatient(patientId: string): Promise<Patient> {
    const basePath = this.getPatientApiUrl(`/${patientId}`);

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          (patient: any) => resolve(patient.patient || patient || []),
          error => reject(error)
        );
    });
  }

  /**
   * Creates or updates a given patient
   *
   * @param {Patient} patient
   * @returns {Promise<Patient>}
   */
  public savePatient(patient: Patient): Promise<Patient> {
    if (patient.uid) {
      return this.updatePatient(patient);
    }

    return this.createPatient(patient);
  }

  /**
   * Updates the patient
   *
   * @param {Patient} patient
   * @returns {Promise<Patient>}
   */
  public createPatient(patient: Patient): Promise<Patient> {
    const basePath = this.getPatientApiUrl();

    return new Promise((resolve, reject) => {
      this.http.post(basePath, patient, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => resolve(<Patient>(response.client || response.patient || response)),
          error => reject(error));
    });
  }

  /**
   * Updates the patient
   *
   * @param {Patient} patient
   * @returns {Promise<Patient>}
   */
  public updatePatient(patient: Patient): Promise<Patient> {
    const basePath = this.getPatientApiUrl(`/${patient.uid}`);

    return new Promise((resolve, reject) => {
      this.http.put(basePath, patient, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(patient),
          error => reject(error));
    });
  }

  /**
   * Deletes a patient
   *
   * @param {Patient} patient
   * @returns {Promise<any>}
   */
  public deletePatient(patient: Patient): Promise<any> {
    const basePath = this.getPatientApiUrl(`/${patient.uid}`);

    return new Promise<void>((resolve, reject) => {
      this.http.delete(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(),
          error => reject(error));
    });
  }

  /**
   * Assigns questionnaires to patient
   * Questionnaires must be an array of questionnaire id's
   *
   * @param {Patient} patient
   * @param {string[]} questionnaires
   */
  public assignQuestionnaires(patient: Patient, questionnaires: string[]): Promise<Questionnaire[]> {
    const basePath = this.getPatientApiUrl(`/${patient.uid}/questionnaire`);

    return new Promise((resolve, reject) => {
      this.http.post(basePath, questionnaires, {headers: this.getAuthHeaders()})
        .subscribe((response: any) => {
          resolve(response.questionnaire || response.rows || response);
        },
        error => {
          reject(error);
        });
    });
  }

  /**
   * Method to load questionnaires.
   *
   * @returns {Promise<Questionnaire[]>}
   */
  public loadQuestionnaires(): Promise<Questionnaire[]> {
    const basePath = this.getQuestionnaireApiUrl() + '?order=meta.title.de_DE&active=true';

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe((response: any) => {
            const questionnaires = response.questionnaire || response.rows || response;
            resolve(questionnaires);
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Method to check if survey mail is configured
   *
   * @returns {Promise<boolean>}
   */
  public isSurveyMailConfigured(): Promise<boolean> {
    const basePath = this.getSurveyMailApiUrl('/configured');

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe((response: any) => resolve(response.success || false), error => reject(error));
    });
  }

  /**
   * Import a survey ZIP to create a new patient (or assign if existing)
   * Mirrors survey-grid import to decouple modules.
   * Returns API result containing clientId on success.
   */
  public importSurveyZip(zipFile: Blob | File, createClient: boolean = true): Promise<{success: boolean, surveyId?: string, clientId?: string}> {
    const query = createClient ? '?createClient=true' : '';
    const url = this.getSurveyApiUrl(`/import${query}`);

    const headers = { ...this.getAuthHeaders(), 'Content-Type': 'application/zip' } as any;

    return new Promise((resolve, reject) => {
      this.http.post(url, zipFile, { headers })
        .subscribe((result: any) => resolve(result), (error) => reject(error));
    });
  }

  /**
   * Fetches number of submitted surveys for current month
   *
   * @returs {Promise<number>}
   */
  public async getNumSubmittedSurveysForCurrentMonth(): Promise<number> {
    const url = this.getPatientApiUrl('/survey/submitted/count');

    return new Promise<number>((resolve, reject) => {
      this.http.get(url, {headers: this.getAuthHeaders()}).subscribe(
        (response: {count: number}) => resolve(response.count || 0),
        error => reject(error)
      );
    });
  }

  /**
   * Loads custom field definitions from settings API.
   * Returns empty array if the endpoint is not available (404/502).
   *
   * @returns {Promise<CustomFieldDefinition[]>}
   */
  public loadCustomFieldDefinitions(): Promise<CustomFieldDefinition[]> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return Promise.resolve([]);
    }

    const basePath = `${settingsUri}/client-custom?filter={}&opts={}`;

    return new Promise((resolve) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => {
            const definitions = response.definitions || response.customFields || response.rows || [];
            resolve(definitions);
          },
          () => {
            // if endpoint not available (404/502), return empty array
            resolve([]);
          }
        );
    });
  }

  /**
   * Loads custom fields layout configuration from settings API.
   * Returns null if no layout is configured.
   *
   * @returns {Promise<CustomFieldsLayout | null>}
   */
  public async loadCustomFieldsLayout(): Promise<CustomFieldsLayout | null> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return null;
    }

    try {
      const [gridSize, positions] = await Promise.all([
        this.getSettingByKey<{cols: number, rows: number}>('customFieldsLayout.gridSize', settingsUri),
        this.getSettingByKey<any[]>('customFieldsLayout.positions', settingsUri)
      ]);

      if (!gridSize && (!positions || positions.length === 0)) {
        return null;
      }

      return {
        gridSize: gridSize || {cols: 6, rows: 6},
        positions: positions || []
      };
    } catch (error) {
      console.error('Failed to load custom fields layout', error);
      return null;
    }
  }

  /**
   * Loads master data layout configuration from settings API.
   * Returns null if no layout is configured.
   *
   * @returns {Promise<MasterDataLayout | null>}
   */
  public async loadMasterDataLayout(): Promise<MasterDataLayout | null> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return null;
    }

    try {
      const [gridSize, positions] = await Promise.all([
        this.getSettingByKey<{cols: number, rows: number}>('masterDataLayout.gridSize', settingsUri),
        this.getSettingByKey<MasterDataFieldPosition[]>('masterDataLayout.positions', settingsUri)
      ]);

      if (!gridSize && (!positions || positions.length === 0)) {
        return null;
      }

      return {
        gridSize: gridSize || {cols: 6, rows: 6},
        positions: positions || []
      };
    } catch (error) {
      console.error('Failed to load master data layout', error);
      return null;
    }
  }

  /**
   * Gets a setting value by key from the settings API.
   *
   * @param key The setting key.
   * @param settingsUri The settings API URI.
   * @returns Promise resolving to the value or null.
   */
  private getSettingByKey<T>(key: string, settingsUri: string): Promise<T | null> {
    const basePath = `${settingsUri}/get`;

    return new Promise((resolve) => {
      this.http.post(basePath, {key}, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => {
            if (response && response.value !== undefined && response.value !== null && response.value !== '') {
              try {
                const parsed = typeof response.value === 'string' ? JSON.parse(response.value) : response.value;
                resolve(parsed as T);
              } catch (e) {
                resolve(response.value as T);
              }
            } else {
              resolve(null);
            }
          },
          () => resolve(null)
        );
    });
  }

  /**
   * Saves a setting value by key to the settings API.
   *
   * @param key The setting key.
   * @param value The value to save (will be JSON stringified).
   * @returns Promise resolving when saved.
   */
  public saveSettingByKey(key: string, value: any): Promise<void> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return Promise.reject(new Error('Settings API not configured'));
    }

    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

    return new Promise((resolve, reject) => {
      this.http.post(settingsUri, {key, value: stringValue}, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(),
          (error) => reject(error)
        );
    });
  }

  /**
   * Deletes a setting by key from the settings API.
   *
   * @param key The setting key.
   * @returns Promise resolving when deleted.
   */
  public deleteSettingByKey(key: string): Promise<void> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return Promise.reject(new Error('Settings API not configured'));
    }

    // save empty value to effectively delete the setting
    return new Promise((resolve, reject) => {
      this.http.post(settingsUri, {key, value: ''}, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(),
          (error) => reject(error)
        );
    });
  }

  /**
   * Loads grid column settings default from settings API.
   *
   * @returns Promise resolving to settings or null.
   */
  public async loadGridColumnSettings(): Promise<{visibility: {[key: string]: boolean}, order: string[], sort: string, sortOrder: 'asc' | 'desc'} | null> {
    const settingsUri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);

    if (!settingsUri) {
      return null;
    }

    return this.getSettingByKey('patientGrid.defaultColumns', settingsUri);
  }

  /**
   * Returns api url for questionnaire
   *
   * @param {string} suffix
   * @returns {string}
   */
  private getQuestionnaireApiUrl(suffix?: string): string {
    return this.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns api url for patient
   *
   * @param {string} suffix
   * @returns {string}
   */
  private getPatientApiUrl(suffix?: string): string {
    return this.buildUrl(configKeys.PATIENT_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns api url for survey mail
   *
   * @param {string} suffix
   * @returns {string}
   */
  private getSurveyMailApiUrl(suffix?: string): string {
    return this.buildUrl(configKeys.SURVEY_MAIL_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns api url for survey
   */
  private getSurveyApiUrl(suffix?: string): string {
    return this.buildUrl(configKeys.SURVEY_API_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns url for config key. Adds auth information to url path when api is not deprecated
   *
   * @param {string} configKey
   * @returns {any}
   */
  private buildUrl(configKey: string, suffix?: string) {
    let uri = this.configService.get(configKey);

    if (suffix) {
      uri = `${uri}${suffix}`;
    }

    return uri;
  }

  /**
   * Returns auth headers by auth token and tenant id
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get(configKeys.SESSION_AUTH_TOKEN);
    const tenantId = this.registryService.get(configKeys.SESSION_TENANT_ID);

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
